import React, { useState, useEffect } from 'react';
import { InputFieldState, ApplicantState, AgencyState, AppointmentGroupState, AppointmentsForRestore, TempSavedValue, FormStateWithEnteredValues } from '../types';
import { PdfCreation, isError } from './utils';
import InstructionsForUsing from '../InstructionsForUsing';
import ImportingDocumentsForm from '../ImportingDocumentsForm';
import EnteringDocumentInformationForm from '../EnteringDocumentInformationForm';
import AppointmentGroups from './appointmentGroups.json';
import { LOCAL_CONSTANT } from '../localConstant';

const pdfMake = window.pdfMake;
const fonts = {
  IPA_mincho: {
    normal: "ipam.ttf",
    bold: "ipam.ttf",
    italics: "ipam.ttf",
    bolditalics: "ipam.ttf",
  },
};

pdfMake.fonts = fonts;

/**
 * 委任事項
 * @type {AppointmentGroup[]}
 */
const initialAppointmentGroups: AppointmentGroupState[] = AppointmentGroups;

/**
 * システム実行を取得する関するです。
 * @returns {string} - システム実行日の文字列
 */
const getToday = (): string => {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = date.getMonth() + 1;
  const dd = date.getDate();
  return `${yyyy}-${mm.toString().padStart(2, '0')}-${dd.toString().padStart(2, '0')}`;
}

/**
 * PDF作成フォームのコンポーネントです。
 * @returns {JSX.Element} - 委任事項（メイン）フォームのJSX要素
 */
const PdfGeneratorForm: React.FC = () => {

  const [documentName, setDocumentName] = useState<InputFieldState>({
    value:'包括委任状提出書',
    errorMessage: ''
  });
  const [submissionDate, setSubmissionDate] = useState<InputFieldState>({ value: getToday(), errorMessage: ''});
  const [delegationDate, setDelegationDate] = useState<InputFieldState>({ value: '', errorMessage: ''});
  const [applicant, setApplicant] = useState<ApplicantState>({
    identificationNumber: {
      value: '',
      errorMessage: ''
    },
    postalCode: {
      value: '',
      errorMessage: ''
    },
    address: {
      value: '',
      errorMessage: ''
    },
    name: {
      value: '',
      errorMessage: ''
    },
    representative: {
      value: '',
      errorMessage: ''
    }
  });
  const [agencies, setAgencies] = useState<AgencyState[]>(
    [
      {
        isProcedureExecutor: true,
        identificationNumber: {
          value: '',
          errorMessage: ''
        },
        address: {
          value: '',
          errorMessage: ''
        },
        name: {
          value: '',
          errorMessage: ''
        },
        representative: {
          value: '',
          errorMessage: ''
        }
      }
    ]
  );
  const [appointmentGroups, setAppointmentGroups] = useState<AppointmentGroupState[]>(initialAppointmentGroups);
  const [otherAppointments, setOtherAppointments] = useState<string[]>(['']);
  const [additinalItems, setAdditionalItems] = useState<string[]>(['包括委任状      １通']);
  const [isSubmitDiabled, setIsSubmitDisabled] = useState<boolean>(true);

  useEffect(() => {
    const isDisabled = isError({
      documentName: documentName,
      submissionDate: submissionDate,
      delegationDate: delegationDate,
      applicant: applicant,
      agencies: agencies,
      appointmentGroups: appointmentGroups,
      otherAppointments: otherAppointments,
      additionalItems: additinalItems
    });
    setIsSubmitDisabled(isDisabled);
  }, [documentName, submissionDate, delegationDate, applicant, agencies, appointmentGroups, otherAppointments, additinalItems]);

  /**
   * フォームをリストアする関数です。
   * @param {TempSavedValue} jsonObject - アップロードされた一次保存データ
   * @returns {void}
   */
  const restoreForm = (jsonObject: TempSavedValue) => {
    setDocumentName({
      value: jsonObject['書類名'],
      errorMessage: ''
    });
    setSubmissionDate({
      value: jsonObject['提出日'],
      errorMessage: ''
    });
    setDelegationDate({
      value: jsonObject['委任日'],
      errorMessage: ''
    });
    setApplicant({
      identificationNumber: {
        value: jsonObject['提出者']['識別番号'],
        errorMessage: ''
      },
      postalCode: {
        value: jsonObject['提出者']['郵便番号'],
        errorMessage: ''
      },
      address: {
        value: jsonObject['提出者']['住所又は居所'],
        errorMessage: ''
      },
      name: {
        value: jsonObject['提出者']['氏名又は名称'],
        errorMessage: ''
      },
      representative: {
        value: jsonObject['提出者']['代表者'],
        errorMessage: ''
      }
    });
    setAgencies(jsonObject['代理人'].map((agency) => {
      return ({
        isProcedureExecutor: agency['手続実行者フラグ'],
        identificationNumber: {
          value: agency['識別番号'],
          errorMessage: ''
        },
        address: {
          value: agency['住所又は居所'],
          errorMessage: ''
        },
        name: {
          value: agency['氏名又は名称'],
          errorMessage: ''
        },
        representative: {
          value: agency['代表者'],
          errorMessage: ''
        }
      });
    }));
    const tempSavedAppointmentGroupsValue: AppointmentGroupState[] = JSON.parse(JSON.stringify(initialAppointmentGroups));
    tempSavedAppointmentGroupsValue.forEach((appointmentGroup) => {
      appointmentGroup.masterAppointment.isChecked = false;
      appointmentGroup.servantAppointments.forEach((servantAppointment) => {
        servantAppointment.isChecked = false;
      });
      appointmentGroup.jurisdictions.forEach((jurisdiction) => {
        jurisdiction.isChecked = false;
      });
    });
    tempSavedAppointmentGroupsValue.forEach((appointmentGroup) => {
      jsonObject['委任事項'].forEach((tempSavedAppointmentGroupValue) => {
        if(tempSavedAppointmentGroupValue['委任事項（メイン）ID'] === appointmentGroup.masterAppointment.appointmentId) {
          appointmentGroup.masterAppointment.isChecked = true;
          tempSavedAppointmentGroupValue['委任事項（サブ）ID'].forEach((servantAppointmentId) => {
            appointmentGroup.servantAppointments.forEach((servantAppointment) => {
              if(servantAppointmentId === servantAppointment.appointmentId) {
                servantAppointment.isChecked = true;
              }
            });
          });
          tempSavedAppointmentGroupValue['法域'].forEach((jurisdictionId) => {
            appointmentGroup.jurisdictions.forEach((jurisdiction) => {
              if(jurisdictionId === jurisdiction.jurisdictionCode) {
                jurisdiction.isChecked = true;
              }
            });
          });
        }
      });
    });
    setAppointmentGroups(tempSavedAppointmentGroupsValue);
    setOtherAppointments(jsonObject['その他の委任事項']);
    setAdditionalItems(jsonObject['提出物件']);
  }

  /**
   * フォームをリセットする関数です。
   * @returns {void}
   */
  const resetForm = (): void => {
    const confirm: boolean = window.confirm('入力内容をリセットしますか？');
    if(confirm === true) {
      setDocumentName({
        value: '包括委任状提出書',
        errorMessage: ''
      });
      setSubmissionDate({
        value: getToday(),
        errorMessage: ''
      });
      setDelegationDate({
        value: '',
        errorMessage: ''
      });
      setApplicant({
        identificationNumber: {
          value: '',
          errorMessage: ''
        },
        postalCode: {
          value: '',
          errorMessage: ''
        },
        address: {
          value: '',
          errorMessage: ''
        },
        name: {
          value: '',
          errorMessage: ''
        },
        representative: {
          value: '',
          errorMessage: ''
        }
      });
      setAgencies(
        [
          {
            isProcedureExecutor: true,
            identificationNumber: {
              value: '',
              errorMessage: ''
            },
            address: {
              value: '',
              errorMessage: ''
            },
            name: {
              value: '',
              errorMessage: ''
            },
            representative: {
              value: '',
              errorMessage: ''
            }
          }
        ]
      );
      setAppointmentGroups(initialAppointmentGroups);
      setOtherAppointments(['']);
      setAdditionalItems(['包括委任状      １通']);
    }
  }

  /**
   * フォームをリストアする関数です。
   * @returns {void}
   */
  const saveForm = () => {
    const tempAppointmentGroupsValue: AppointmentsForRestore[] = [];
    appointmentGroups.forEach((appointmentGroup) => {
      if(appointmentGroup.masterAppointment.isChecked === true) {
        const tempServantAppointmentIds: string[] = [];
        appointmentGroup.servantAppointments.forEach((servantAppointment) => {
          if(servantAppointment.isChecked === true) {
            tempServantAppointmentIds.push(servantAppointment.appointmentId);
          }
        });
        const tempJurisdicionts: string[] = [];
        appointmentGroup.jurisdictions.forEach((jurisdiction) => {
          if(jurisdiction.isChecked === true) {
            tempJurisdicionts.push(jurisdiction.jurisdictionCode);
          }
        });
        tempAppointmentGroupsValue.push({
          '委任事項（メイン）ID': appointmentGroup.masterAppointment.appointmentId,
          '委任事項（サブ）ID': tempServantAppointmentIds,
          '法域': tempJurisdicionts
        });
      }
    });
    const tempFormValue: TempSavedValue = {
      '書類名': documentName.value,
      '提出日': submissionDate.value,
      '委任日': delegationDate.value,
      '提出者': {
        '識別番号': applicant.identificationNumber.value,
        '郵便番号': applicant.postalCode.value,
        '住所又は居所': applicant.address.value,
        '氏名又は名称': applicant.name.value,
        '代表者': applicant.representative.value
      },
      '代理人': agencies.map((agency) => {
        return ({
          '手続実行者フラグ': agency.isProcedureExecutor,
          '識別番号': agency.identificationNumber.value,
          '住所又は居所': agency.address.value,
          '氏名又は名称': agency.name.value,
          '代表者': agency.representative.value
        });
      }),
      '委任事項': tempAppointmentGroupsValue,
      'その他の委任事項': otherAppointments,
      '提出物件': additinalItems
    };

    const blob = new Blob([JSON.stringify(tempFormValue)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
  
    const link = document.createElement('a');
    link.href = url;
    link.download = `${documentName.value}（特殊申請書類作成支援ツール）_${getToday().replace(/-/g, '')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * 包括委任状提出書のPDFを作成する関数です。
   * @returns {void}
   */
  const createPdfMain = () => {
    const formValue: FormStateWithEnteredValues = {
      documentName: documentName,
      submissionDate: submissionDate,
      delegationDate: delegationDate,
      applicant: applicant,
      agencies: agencies,
      appointmentGroups: appointmentGroups,
      otherAppointments: otherAppointments,
      additionalItems: additinalItems
    }

    const pdfCreation = new PdfCreation(formValue);
    if(pdfCreation.isError()) {
      window.alert('入力内容に不備があります。');
      return;
    }

    const documentDefinition = pdfCreation.getFormValue(1);
  
    const pdf = pdfMake.createPdf(documentDefinition);
    pdf.download(`包括委任状提出書（特殊申請書類作成支援ツール）_${getToday().replace(/-/g, '')}.pdf`);
  }

  /**
   * 包括委任状のPDFを作成する関数です。
   * @returns {void}
   */
  const createPdfSub = () => {
    const formValue: FormStateWithEnteredValues = {
      documentName: documentName,
      submissionDate: submissionDate,
      delegationDate: delegationDate,
      applicant: applicant,
      agencies: agencies,
      appointmentGroups: appointmentGroups,
      otherAppointments: otherAppointments,
      additionalItems: additinalItems
    }
    const pdfCreation = new PdfCreation(formValue);
    const documentDefinition = pdfCreation.getFormValue(2);
    const warningMessages: string[] = [];
    if(pdfCreation.hasSpecificAppointmentId('40')) {
      warningMessages.push(LOCAL_CONSTANT.MESSAGE_INCLUDING_PCT_PROCEDURES);
    }
    warningMessages.push(LOCAL_CONSTANT.MESSAGE_IMPORTANT_NOTICE_REGARDING_USAGE);
    const confirm = window.confirm(warningMessages.join('\n\n'));
    if(confirm) {
      const pdf = pdfMake.createPdf(documentDefinition);
      pdf.download(`包括委任状（特殊申請書類作成支援ツール）_${getToday().replace(/-/g, '')}.pdf`);
    }
  }

  return (
    <>
      <InstructionsForUsing />
      <hr />
      <ImportingDocumentsForm
        restoreForm={restoreForm}
        resetForm={resetForm}
      />
      <hr />
      <EnteringDocumentInformationForm
        documentName={documentName}
        setDocumentName={setDocumentName}
        submissionDate={submissionDate}
        setSubmissionDate={setSubmissionDate}
        delegationDate={delegationDate}
        setDelegationDate={setDelegationDate}
        applicant={applicant}
        setApplicant={setApplicant}
        agencies={agencies}
        setAgencies={setAgencies}
        appointmentGroups={appointmentGroups}
        setAppointmentGroups={setAppointmentGroups}
        otherAppointments={otherAppointments}
        setOtherAppointments={setOtherAppointments}
        additinalItems={additinalItems}
        setAdditionalItems={setAdditionalItems}
        saveForm={saveForm}
        resetForm={resetForm}
        isSubmitDiabled={isSubmitDiabled}
        createPdfMain={createPdfMain}
        createPdfSub={createPdfSub}
      />
    </>
  );
}

export default PdfGeneratorForm;